import RESTAdapter from '@ember-data/adapter/rest';
import { isEmpty } from '@ember/utils';
import { computed, set, get } from '@ember/object';
import { resolve, all } from 'rsvp';
import { decamelize } from '@ember/string';
import { getCookie } from '@square/dashboard-utilities/utils/cookies';
import { BatchGetContactsRequest } from '@square/dashboard-data/models/squareup/rolodex/contact';
import { batchGetContacts } from '@square/dashboard-data/clients/rolodex-contact';
import ContactOptions from '@square/dashboard-data/models/squareup/rolodex/contact-options';
import { MULTIPLE_IMAGES_API_VERSION as SUBSCRIPTION_PAUSE_RESUME_API_VERSION } from '@square/dashboard-items-data/adapters/connect/image';
import { SUBSCRIPTION_ITEMIZATION_API_VERSION } from '@square/dashboard-items-data/models/connect/subscription-plan';
import { inject as service } from '@ember/service';
import { baseConnectUrl } from '@square/dashboard-utilities/utils/url';

// eslint-disable-next-line ember/no-classic-classes
export default RESTAdapter.extend({
  init(args) {
    this._super(args);
    this.useFetch = false;
  },
  modelName: 'buyer-sub',
  namespace: 'v2',
  host: baseConnectUrl(''),
  pathForType() {
    return 'subscriptions';
  },
  orderService: service('buyer-subs/order'),
  itemizationStatus: service('buyer-subs/itemization-status'),
  userInfo: service(),
  features: service(),
  headers: computed('itemizationStatus.isItemized', function () {
    let version = SUBSCRIPTION_PAUSE_RESUME_API_VERSION;
    if (this.itemizationStatus.isItemized || this.features.isEnabled('buyersubs/updated_legacy_api_version')) {
      version = SUBSCRIPTION_ITEMIZATION_API_VERSION;
    }
    return {
      'X-CSRF-Token': getCookie('_js_csrf'),
      'Square-Version': version
    };
  }),
  fetchContacts(contactToken, merchantToken) {
    if (isEmpty(contactToken)) {
      return resolve({
        contactsMap: {}
      });
    }
    const batchGetContactsRequest = BatchGetContactsRequest.create({
      merchantToken: [merchantToken],
      contactToken,
      asMap: true,
      contactOptions: ContactOptions.create({
        includeBuyerSummary: false,
        includeInstrumentsOnFile: false
      })
    });
    return batchGetContacts(batchGetContactsRequest);
  },
  async query(store, type, query) {
    const adapterOptions = {
      ...query.adapterOptions
    };
    // eslint-disable-next-line no-param-reassign
    delete query.adapterOptions;
    await this.itemizationStatus.load();
    const url = this.buildURL(type.modelName, null, null, 'query', query);
    const response = await this.ajax(url, 'POST', {
      data: query,
      xhrFields: {
        withCredentials: true
      }
    });
    if (adapterOptions?.excludeRelatedObjects) {
      return response;
    }
    if (!isEmpty(response.subscriptions)) {
      const fetchContacts = this.fetchContacts([...new Set(response.subscriptions?.map(subscription => subscription.customer_id))], this.userInfo.merchantToken);
      const fetchPlans = store.findAll('connect/subscription-plan');
      let fetchPlanVariations;
      if (this.itemizationStatus.isItemized || this.features.isEnabled('buyersubs/updated_legacy_api_version')) {
        // For itemized sellers, we will fetch all plans and all variations, in case they have any variations
        // without parent plans. Eventually when variations are constraint to require parent plans, we can just
        // fetch the plans
        fetchPlanVariations = store.findAll('connect/subscription-plan/plan-variation');
      }

      // Get top-level order templates
      let orderTemplateIds = response.subscriptions?.map(sub => sub.order_template_id).filter(Boolean);
      // Add order templates from phases
      const phaseTemplateIds = response.subscriptions?.map(sub => sub.phases?.map(phase => phase.order_template_id))?.flat()?.filter(Boolean);
      orderTemplateIds = [...orderTemplateIds, ...phaseTemplateIds];
      const fetchOrderTemplates = orderTemplateIds?.length > 0 ? store.query('subscription-order-template', {
        ids: [...orderTemplateIds]
      }) : Promise.resolve();
      const [contacts, plans, orderTemplates, planVariations] = await all([fetchContacts, fetchPlans, fetchOrderTemplates, fetchPlanVariations]);
      let planMap;
      let planKey = 'plan';
      if (this.itemizationStatus.isItemized || this.features.isEnabled('buyersubs/updated_legacy_api_version')) {
        planKey = 'planVariation';
        planMap = planVariations.reduce((map, variation) => map.set(variation.id, variation), new Map());
      } else {
        planMap = plans.reduce((map, plan) => map.set(plan.id, plan), new Map());
      }
      const contactsMap = contacts.contactsMap;
      const ordersMap = orderTemplates?.reduce((map, order) => map.set(order.id, order), new Map());
      response.subscriptions.forEach(subscription => {
        const customer = contactsMap[subscription.customer_id];
        if (customer) {
          set(subscription, 'customer_name', customer.displayName);
        } else {
          set(subscription, 'deleted_contact', true);
        }
        set(subscription, planKey, planMap.get(get(subscription, 'plan.id')));
        if (subscription.order_template_id) {
          const order = ordersMap.get(subscription.order_template_id);
          set(subscription, 'order_template_total_money', order.totalMoney);
        }
      });
    }
    return response;
  },
  urlForFindAll(modelName) {
    return this.urlForQuery(null, modelName);
  },
  urlForFindMany(modelName) {
    return this.urlForQuery(null, modelName);
  },
  urlForQuery(query, modelName) {
    const baseUrl = this.buildURL(modelName);
    return `${baseUrl}/search`;
  },
  urlForFindRecord(id, modelName, snapshot) {
    let url = `${this.buildURL(modelName)}/${id}`;
    const includeActions = snapshot.adapterOptions?.includeActions;
    if (includeActions) {
      url += '?include=actions';
    }
    return url;
  },
  urlForCreateRecord(modelName) {
    const baseUrl = this.buildURL(modelName);
    return `${baseUrl}`;
  },
  urlForUpdateRecord(id, modelName) {
    const baseUrl = this.buildURL(modelName);
    return `${baseUrl}/${id}`;
  },
  urlForDeleteRecord(id, modelName) {
    const baseUrl = this.buildURL(modelName);
    return `${baseUrl}/${id}`;
  },
  updateRecord(store, type, snapshot) {
    const subscription = snapshot.record.serialize();
    const fieldsToClear = [];
    if (this.features.isEnabled('buyersubs/updated_legacy_api_version')) {
      delete subscription.plan_id;
    }
    const changedAttributes = snapshot.record.changedAttributes();
    for (const key in changedAttributes) {
      if (isEmpty(changedAttributes[key][1])) {
        fieldsToClear.push(decamelize(key));
      }
    }
    const data = {
      subscription_id: snapshot.id,
      subscription,
      fields_to_clear: fieldsToClear
    };
    const url = this.buildURL(type.modelName, snapshot.id, snapshot, 'updateRecord');
    return this.ajax(url, 'PUT', {
      data,
      xhrFields: {
        withCredentials: true
      }
    });
  },
  // Catalog replaces temp ids with the real ones
  // We aren't using catalog here so we replace it ourselves
  async createRecord(store, type, snapshot) {
    snapshot.record.set('source', {
      name: 'Square Subscriptions Dashboard Web'
    });
    const data = snapshot.record.serialize();
    const url = this.buildURL(type.modelName, null, snapshot, 'createRecord');
    const results = await this.ajax(url, 'POST', {
      data,
      xhrFields: {
        withCredentials: true
      }
    });
    const record = snapshot.record;
    record.set('id', results.subscription?.id);
    return results;
  },
  shouldBackgroundReloadRecord() {
    return false;
  },
  findRecord(store, type, id, snapshot) {
    const url = this.buildURL(type.modelName, id, snapshot, 'findRecord');
    let data = null;
    if (snapshot.adapterOptions?.includeRelatedObjects !== undefined) {
      data = {
        include_related_objects: snapshot.adapterOptions.includeRelatedObjects
      };
    }
    return this.ajax(url, 'GET', {
      data,
      xhrFields: {
        withCredentials: true
      }
    });
  },
  findAll(store, type) {
    const url = this.buildURL(type.modelName, null, null, 'findAll', null);
    return this.ajax(url, 'GET');
  },
  deleteRecord(record) {
    const url = this.buildURL(this.modelName, record.id, 'deleteRecord');
    return this.ajax(url, 'DELETE', {
      xhrFields: {
        withCredentials: true
      }
    });
  }
});